import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { HeadlineLevel } from '@aok/text-elements'

import defaultProps from '../../../utils/propTypes/defaultProps'
import Slider from '../../molecules/Slider/Slider'
import ModuleContainer from '../../_layout/Module/ModuleContainer'
import LinkHeadline from '../../molecules/LinkHeadline/LinkHeadline'
import TripleTeaserS from '../../molecules/TripleTeaser/TripleTeaser-S'

import { ColorScheme, ComponentVariant } from '../../../utils/defaultTypes'

const Y004S = ({ headline, elements, settings, dataTrackingName }) => {
  let bgColorContainer

  if (settings.colorScheme === ColorScheme.STRUCTURE) {
    bgColorContainer = (
      <>
        <div
          className="
          bg-e8f4f2
          absolute z-0 top-0 -left-1/2
          w-screenxl h-3/10
          480:h-3/8
          600:-translate-x-1/2 600:left-0 600:h-1/4
          840:h-3/10
          1280:h-3/10
        "
        ></div>
      </>
    )
  }

  const sliderSettings = {
    slides: {
      perView: 1.2,
      spacing: 16,
    },
    breakpoints: {
      '(min-width: 480px)': {
        slides: {
          perView: 1.3,
          spacing: 16,
        },
      },
      '(min-width: 600px)': {
        slides: {
          perView: 2.2,
          spacing: 16,
        },
      },
      '(min-width: 720px)': {
        slides: {
          perView: 2.2,
          spacing: 24,
        },
      },
      '(min-width: 840px)': {
        slides: {
          perView: 2.4,
          spacing: 24,
        },
      },
      '(min-width: 1000px)': {
        slides: {
          perView: 3,
          spacing: 32,
        },
      },
    },
  }

  const paginationSettings = {
    colorScheme: 'light',
  }

  const [sliderEnabled, setSliderEnabled] = React.useState(false)
  const [teaserCount, setTeaserCount] = useState(0)

  useEffect(() => {
    if (elements && teaserCount !== elements.length) {
      setTeaserCount(elements.length)

      if (1 < elements.length) {
        setSliderEnabled(true)
      } else {
        setSliderEnabled(false)
      }
    }
  }, [elements, teaserCount, sliderEnabled])

  // mb and pb hack necessary to avoid clipping focus outline around teaser elements at the bottom of the module
  return (
    <div data-tracking-name={dataTrackingName} className="y004 -s w-full pb-8 -mb-8 overflow-hidden">
      <div
        className={`
          ${settings.colorScheme === ColorScheme.STRUCTURE ? 'bg-e8f4f2 pt-64 pb-32 720:pt-80 1000:pt-112' : 'pb-32'}
        `}
      >
        <ModuleContainer>
          <LinkHeadline headline={headline} />
        </ModuleContainer>
      </div>
      <ModuleContainer className="relative flex flex-col gap-32">
        <HeadlineLevel>
          {sliderEnabled ? (
            <Slider
              elements={elements}
              paginationClasses="
              absolute
              left-1/2 -translate-x-1/2
              600:left-auto 600:translate-x-0
              600:right-16 720:right-24 1000:right-32
              1280:right-0"
              paginationOuterClasses="mb-64 600:mb-80 1280:relative 1280:mx-auto 1280:w-1216"
              teaserCount={teaserCount}
              sliderSettings={sliderSettings}
              sliderClassName="relative z-1"
              paginationSettings={paginationSettings}
              allowPaginationOffset={false}
            >
              {elements.map((element, index) => (
                <Fragment key={index}>
                  <TripleTeaserS element={element} settings={settings} />
                </Fragment>
              ))}
            </Slider>
          ) : (
            <div className="relative w-full">
              {elements.map((element, index) => (
                <Fragment key={index}>
                  <TripleTeaserS element={element} settings={settings} />
                </Fragment>
              ))}
            </div>
          )}
          {bgColorContainer}
        </HeadlineLevel>
      </ModuleContainer>
    </div>
  )
}

Y004S.propTypes = {
  headline: PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
  }),
  elements: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      abstract: PropTypes.string,
      url: PropTypes.string,
      image: PropTypes.shape({
        alt: PropTypes.string,
        variants: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            breakpoint: PropTypes.number,
          })
        ),
      }),
    })
  ),
  settings: PropTypes.shape({
    variant: PropTypes.oneOf([ComponentVariant.SMALL, ComponentVariant.MEDIUM, ComponentVariant.LARGE]),
    colorScheme: PropTypes.oneOf([ColorScheme.LIGHT, ColorScheme.STRUCTURE]),
  }),
  dataTrackingName: PropTypes.string,
}

Y004S.defaultProps = {
  headline: {
    text: defaultProps.HEADLINE,
    url: defaultProps.URL,
  },
  elements: [
    {
      title: defaultProps.TITLE,
      subtitle: defaultProps.SUBTITLE,
      abstract: defaultProps.DESCRIPTION,
      url: defaultProps.URL,
      image: {
        alt: defaultProps.IMAGE_ALT,
        variants: [],
      },
    },
  ],
  settings: {
    variants: 'small',
    colorScheme: 'light',
  },
}

export default Y004S
